import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, Inbox, Send } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Skeleton } from '@/components/ui/Skeleton'
import { Badge } from '@/components/ui/Badge'
import { formatDateTime } from '@/lib/format'
import { useHandovers, useShiftSummary } from './hooks'
import type { HandoverListFilters, HandoverStatus, HandoverSummary } from './types'

const SLOW_ACK_MIN = 30

const STATUSES: { value: HandoverStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'acknowledged', label: 'Acknowledged' },
]

/**
 * AF-135 — historical log + inbox.
 *
 * Shown to anyone with `handovers:read`. Team leads use the date /
 * team / user filters; the inbox surfaces any inbound handovers
 * awaiting this user's acknowledgement.
 */
export function HandoverLogPage() {
  const summary = useShiftSummary()
  const inbound = summary.data?.inbound ?? []

  const [filters, setFilters] = useState<HandoverListFilters>({})
  const list = useHandovers(filters)

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Handovers</h1>
          <p className="text-sm text-muted-fg">
            Inbound handovers awaiting your acknowledgement, plus the
            historical log for team-lead review.
          </p>
        </div>
        <Button asChild>
          <Link to="/handovers/end-shift">
            <Send size={14} aria-hidden /> Start end-of-shift handover
          </Link>
        </Button>
      </header>

      {inbound.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Inbox size={14} aria-hidden /> Awaiting your acknowledgement (
              {inbound.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-1">
            {inbound.map((h) => (
              <Link
                key={h.id}
                to={`/handovers/${h.id}`}
                className="grid grid-cols-[1fr_auto_auto_1rem] items-center gap-3 rounded border border-status-pending/30 bg-status-pending/5 px-3 py-2 text-sm transition-colors hover:bg-status-pending/10"
              >
                <span className="flex flex-col">
                  <span className="font-medium">
                    From {h.outgoing.display_name}
                  </span>
                  <span className="text-xs text-muted-fg">
                    {h.submitted_at && formatDateTime(h.submitted_at)} · Shift{' '}
                    {h.shift_id}
                  </span>
                </span>
                <Badge variant="pending">Awaiting</Badge>
                <span className="text-xs text-muted-fg">
                  {h.counts.critical_items_count} critical ·{' '}
                  {h.counts.sla_at_risk_count} SLA at risk
                </span>
                <ChevronRight size={12} className="text-muted-fg" aria-hidden />
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      <FiltersCard value={filters} onChange={setFilters} />

      {list.isLoading && <Skeleton className="h-48 w-full" />}
      {list.isError && (
        <p className="rounded-md border border-status-failed/30 bg-status-failed/5 p-3 text-sm text-status-failed">
          Couldn't load handovers.
        </p>
      )}
      {list.data && <HistoryTable items={list.data.handovers} />}
    </div>
  )
}

function FiltersCard({
  value,
  onChange,
}: {
  value: HandoverListFilters
  onChange: (v: HandoverListFilters) => void
}) {
  const setStatus = (s: HandoverStatus) => {
    const cur = value.status ?? []
    const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]
    onChange({ ...value, status: next })
  }
  return (
    <Card>
      <CardContent className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="ho-from">From</Label>
          <Input
            id="ho-from"
            type="date"
            value={value.date_from?.slice(0, 10) ?? ''}
            onChange={(e) =>
              onChange({
                ...value,
                date_from: e.target.value
                  ? `${e.target.value}T00:00:00.000Z`
                  : undefined,
              })
            }
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="ho-to">To</Label>
          <Input
            id="ho-to"
            type="date"
            value={value.date_to?.slice(0, 10) ?? ''}
            onChange={(e) =>
              onChange({
                ...value,
                date_to: e.target.value
                  ? `${e.target.value}T23:59:59.999Z`
                  : undefined,
              })
            }
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="ho-team">Team</Label>
          <Input
            id="ho-team"
            value={value.team?.[0] ?? ''}
            onChange={(e) =>
              onChange({
                ...value,
                team: e.target.value ? [e.target.value] : undefined,
              })
            }
            placeholder="fraud-ops"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="ho-user">User</Label>
          <Input
            id="ho-user"
            value={value.user_id?.[0] ?? ''}
            onChange={(e) =>
              onChange({
                ...value,
                user_id: e.target.value ? [e.target.value] : undefined,
              })
            }
            placeholder="user-1"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide text-muted-fg">
            Status
          </span>
          <div className="flex flex-wrap gap-1.5">
            {STATUSES.map((s) => {
              const active = (value.status ?? []).includes(s.value)
              return (
                <button
                  key={s.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setStatus(s.value)}
                  className={
                    'rounded-full border px-2.5 py-1 text-xs ' +
                    (active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-fg hover:bg-muted')
                  }
                >
                  {s.label}
                </button>
              )
            })}
          </div>
        </div>
        {Object.values(value).some((v) => v && (Array.isArray(v) ? v.length > 0 : true)) && (
          <Button variant="ghost" size="sm" onClick={() => onChange({})}>
            Clear filters
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

function HistoryTable({ items }: { items: HandoverSummary[] }) {
  const sorted = useMemo(
    () =>
      [...items].sort((a, b) =>
        (b.submitted_at ?? '').localeCompare(a.submitted_at ?? ''),
      ),
    [items],
  )
  if (sorted.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-fg">
        No handovers match the current filters.
      </p>
    )
  }
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-fg">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-medium">Submitted</th>
              <th scope="col" className="px-3 py-2 text-left font-medium">Outgoing</th>
              <th scope="col" className="px-3 py-2 text-left font-medium">Incoming</th>
              <th scope="col" className="px-3 py-2 text-left font-medium">Team</th>
              <th scope="col" className="px-3 py-2 text-left font-medium">Status</th>
              <th scope="col" className="px-3 py-2 text-right font-medium">Ack time</th>
              <th scope="col" className="w-4 px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((h) => {
              const slow =
                h.ack_minutes !== null && h.ack_minutes > SLOW_ACK_MIN
              return (
                <tr key={h.id} className="border-t border-border">
                  <td className="px-3 py-2 align-middle text-xs text-muted-fg">
                    {h.submitted_at ? formatDateTime(h.submitted_at) : '—'}
                  </td>
                  <td className="px-3 py-2 align-middle">
                    {h.outgoing.display_name}
                  </td>
                  <td className="px-3 py-2 align-middle">
                    {h.incoming?.display_name ?? '—'}
                  </td>
                  <td className="px-3 py-2 align-middle text-muted-fg">
                    {h.team}
                  </td>
                  <td className="px-3 py-2 align-middle capitalize">
                    {h.status.replace('_', ' ')}
                  </td>
                  <td
                    className={
                      'px-3 py-2 text-right align-middle font-mono tabular-nums ' +
                      (slow ? 'text-status-failed font-semibold' : 'text-muted-fg')
                    }
                  >
                    {h.ack_minutes === null
                      ? h.status === 'submitted'
                        ? 'pending'
                        : '—'
                      : `${h.ack_minutes} min`}
                  </td>
                  <td className="px-3 py-2 text-right align-middle">
                    <Link
                      to={`/handovers/${h.id}`}
                      className="text-primary hover:underline"
                      aria-label={`Open handover ${h.id}`}
                    >
                      <ChevronRight size={14} aria-hidden />
                    </Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}
